import { Avatar } from '@aecom-ai-engineering/react-components';
import { Checkbox } from '@consigli/facade';
import { FC, RefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { getInitials } from '../utils/participant-utils';

export interface AttendeeOption {
  id: string;
  email: string;
  name?: string | null;
  isOrganizer: boolean;
}

interface AttendeePickerPopoverProps {
  attendees: AttendeeOption[];
  /** Lowercased emails currently in the recipient list. */
  selectedEmails: ReadonlySet<string>;
  /** True when the recipient list is full, so no further attendee can be added. */
  isFull: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  onToggle: (email: string) => void;
  onToggleAll: () => void;
  onClose: () => void;
}

export const AttendeePickerPopover: FC<AttendeePickerPopoverProps> = ({
  attendees,
  selectedEmails,
  isFull,
  anchorRef,
  onToggle,
  onToggleAll,
  onClose,
}) => {
  const { t } = useTranslation();
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Close the picker only; the modal keeps its own state.
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [anchorRef, onClose]);

  const selectedCount = attendees.filter((a) => selectedEmails.has(a.email)).length;
  const allSelected = attendees.length > 0 && selectedCount === attendees.length;

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={t('meeting-secretary.meetings.email-attendees')}
      className="absolute right-0 top-full z-30 mt-2 w-80 rounded-lg border border-gray-200 bg-white shadow-lg"
    >
      <div className="border-b border-gray-100 px-3 py-1">
        <Checkbox
          id="email-attendees-all"
          checked={allSelected}
          partiallySelected={selectedCount > 0 && !allSelected}
          disabled={isFull && !allSelected && selectedCount === 0}
          onChange={onToggleAll}
          label={t('meeting-secretary.meetings.email-all-attendees', { total: attendees.length })}
        />
      </div>
      <ul className="max-h-60 overflow-y-auto px-1 py-1">
        {attendees.map((attendee) => {
          const checked = selectedEmails.has(attendee.email);
          const display = attendee.name?.trim() || attendee.email;
          return (
            <li key={attendee.email}>
              <label
                htmlFor={`email-attendee-${attendee.id}`}
                className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-gray-50"
              >
                <Checkbox
                  id={`email-attendee-${attendee.id}`}
                  checked={checked}
                  disabled={isFull && !checked}
                  onChange={() => onToggle(attendee.email)}
                  ariaLabel={display}
                />
                <Avatar initials={getInitials(attendee.name, attendee.email)} label={display} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900">
                    {display}
                    {attendee.isOrganizer && (
                      <span className="ml-2 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-normal text-gray-600">
                        {t('meeting-secretary.meetings.email-organizer')}
                      </span>
                    )}
                  </span>
                  {attendee.name?.trim() && (
                    <span className="block truncate text-xs text-gray-500">{attendee.email}</span>
                  )}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
